/**
 * A marca de homologação de cada coluna na tela (onda 313, R7; RN-252) — uma função só, para a tela do
 * LabManager e para o Portal (317).
 *
 * O RN-252 limita o PDF, a planilha e o Portal; a tela é a ferramenta do engenheiro e mostra o que ele
 * precisa ver, marcado:
 *  · homologada (e a leitura do equipamento, `entrada`): sem marca;
 *  · `sem_gabarito` (N1(60), kOCR, Schneider) e o que as premissas tiram da homologação
 *    (`grandezaHomologada(c, premissas)` falso): «▲ não homologada» e o porquê — o da situação vem da
 *    própria conformidade (`CONFORMIDADE.colunas[c].tolerancia`, o trecho até o primeiro «:» ou «;»),
 *    nunca de frase fixa; o das premissas é «fora das condições homologadas: <rótulo da condição>»;
 *  · `nao_confere` (a porosidade): não aparece — fica fora até ser explicada.
 * O Portal chama com `mostrarNaoHomologadas = false` nos componentes: só o homologado aparece.
 */
import {
  CONFORMIDADE, INSUMOS_DA_DISSIPACAO, dissipacaoHomologada, grandezaHomologada, premissasEfetivas,
} from '../motor';
import type { ColunaDoMotor, Premissas, SaidaDaDissipacao } from '../motor';

/** O que a tela faz com uma coluna: mostrar ou não, com a marca e o porquê. */
export interface MarcaDeHomologacao {
  /** A coluna aparece na tela (a `nao_confere` não aparece). */
  mostrar: boolean;
  /** Homologada com estas premissas: sem marca. */
  homologada: boolean;
  /** Por que não é homologada (null quando é). */
  motivo: string | null;
}

/** A marca escrita junto do que não é homologado. */
export const MARCA_NAO_HOMOLOGADA = '▲ não homologada';

/** O trecho da tolerância até o primeiro «:» ou «;» — o porquê da situação, como a conformidade escreve. */
const motivoDaSituacao = (tolerancia: string): string => tolerancia.split(/[:;]/)[0].trim();

/** Os rótulos das condições de valor exigido que as premissas deixam e que tiram alguma das colunas. */
function condicoesQueTiram(colunas: readonly ColunaDoMotor[], premissas: Partial<Premissas>): string[] {
  const p = premissasEfetivas(premissas);
  const rotulos: string[] = [];
  for (const c of CONFORMIDADE.condicoes) {
    if (c.efeito !== 'valor_exigido' || p[c.premissa] === c.valor) continue;
    if (colunas.some((col) => c.afeta.includes(col)) && !rotulos.includes(c.rotulo)) rotulos.push(c.rotulo);
  }
  return rotulos;
}

/** «fora das condições homologadas: γ fixo; média móvel diferente de 3 leituras». */
const foraDasCondicoes = (rotulos: string[]): string => `fora das condições homologadas: ${rotulos.join('; ')}`;

/**
 * A marca de uma coluna do `Resultado` com as premissas da interpretação (`r.premissas`): homologada sem
 * marca; sem gabarito com o porquê da conformidade (e, se as premissas também a tiram, as condições);
 * fora das condições homologadas com o rótulo de cada condição; a que não confere, escondida.
 */
export function marcaDaColuna(c: ColunaDoMotor, premissas: Premissas): MarcaDeHomologacao {
  const conformidade = Object.prototype.hasOwnProperty.call(CONFORMIDADE.colunas, c) ? CONFORMIDADE.colunas[c] : null;
  if (!conformidade || conformidade.situacao === 'nao_confere') {
    return { mostrar: false, homologada: false, motivo: 'não confere com o gabarito: fica fora até ser explicada' };
  }
  const condicoes = condicoesQueTiram([c], premissas);
  if (conformidade.situacao === 'sem_gabarito') {
    const motivo = motivoDaSituacao(conformidade.tolerancia);
    return { mostrar: true, homologada: false, motivo: condicoes.length > 0 ? `${motivo}; ${foraDasCondicoes(condicoes)}` : motivo };
  }
  if (grandezaHomologada(c, premissas)) return { mostrar: true, homologada: true, motivo: null };
  return { mostrar: true, homologada: false, motivo: foraDasCondicoes(condicoes) };
}

/**
 * Uma marca só para um desenho que depende de várias colunas (um ábaco: os dois eixos e a cor dos pontos):
 * homologada só com todas homologadas; o porquê junta o de cada uma, uma vez cada, com o rótulo que quem
 * chama dá a ela («a cor dos pontos (SBTn): …»); escondida se alguma não aparece.
 */
export function combinarMarcas(partes: readonly { marca: MarcaDeHomologacao; rotulo?: string }[]): MarcaDeHomologacao {
  const motivos: string[] = [];
  for (const { marca, rotulo } of partes) {
    if (marca.homologada || !marca.motivo) continue;
    const texto = rotulo ? `${rotulo}: ${marca.motivo}` : marca.motivo;
    if (!motivos.includes(texto)) motivos.push(texto);
  }
  const homologada = partes.every((x) => x.marca.homologada);
  return {
    mostrar: partes.every((x) => x.marca.mostrar),
    homologada,
    motivo: homologada ? null : (motivos.join('; ') || null),
  };
}

/**
 * As colunas homologadas que deixam de ir ao PDF e ao Portal com estas premissas (RN-252) — a conta ÚNICA das telas
 * (a aba Premissas, o ⓘ e as janelas dela, e o «Simular» da vertical): a coluna que já não é homologada (sem
 * gabarito, a que não confere) não «sai», e a leitura do equipamento (`entrada`) nunca sai. Quem quer o efeito de UMA
 * premissa passa só ela (`{ mediaMovel: 5 }`): as outras valem o padrão. A resposta é a do motor
 * (`grandezaHomologada` com as premissas), na ordem das colunas da conformidade.
 */
export function colunasQueSaemDaHomologacao(premissas: Partial<Premissas>): ColunaDoMotor[] {
  const p = premissasEfetivas(premissas);
  return (Object.keys(CONFORMIDADE.colunas) as ColunaDoMotor[])
    .filter((c) => CONFORMIDADE.colunas[c].situacao === 'homologada' && !grandezaHomologada(c, p));
}

/** Os insumos da dissipação que o engenheiro pode informar à mão (os demais vêm da vertical). */
export type InsumoDaDissipacao = 'u0' | 'g0' | 'su' | 'm';

/**
 * A marca de uma saída da dissipação (t₅₀, Ir, cₕ, kₕ). Com todos os insumos da saída informados à mão,
 * homologa-se a conta (`dissipacaoHomologada(s, null)`); com algum da vertical, as premissas dela
 * (`r.premissas`) decidem — e o porquê são as condições que tiram os insumos que vieram da vertical.
 */
export function marcaDaSaidaDaDissipacao(
  s: SaidaDaDissipacao,
  x: { premissas: Premissas; informados: ReadonlySet<InsumoDaDissipacao> },
): MarcaDeHomologacao {
  if (!Object.prototype.hasOwnProperty.call(CONFORMIDADE.dissipacoes.situacao, s)
    || CONFORMIDADE.dissipacoes.situacao[s] !== 'homologada') {
    return { mostrar: false, homologada: false, motivo: 'não confere com o gabarito: fica fora até ser explicada' };
  }
  const daVertical = INSUMOS_DA_DISSIPACAO[s].filter((c) => !x.informados.has(c as InsumoDaDissipacao));
  const homologada = dissipacaoHomologada(s, daVertical.length === 0 ? null : x.premissas);
  if (homologada) return { mostrar: true, homologada: true, motivo: null };
  return { mostrar: true, homologada: false, motivo: foraDasCondicoes(condicoesQueTiram(daVertical, x.premissas)) };
}
